import { useMemo, useState } from 'react'
import {
  useAdminActivity,
  useAdminOverview,
  useAdminUsers,
  useCompanies,
  useCreateUser,
  useResetPassword,
  useRevokeSessions,
  useUpdateUser,
} from '../api/hooks'
import { useAuth } from '../auth/AuthProvider'
import type { ActivityEntry, AdminUser } from '../api/types'
import { formatDate, relativeTime } from '../lib/dates'
import { Avatar } from '../components/TaskRow'
import {
  Button,
  Chip,
  ErrorNotice,
  Loading,
  PageHeader,
  Stat,
  TabStrip,
  cx,
} from '../components/ui'

/** Labels stay lowercase; the strip's `capitalize` is what renders them. */
const TABS = [
  { id: 'overview', label: 'overview' },
  { id: 'people', label: 'people' },
  { id: 'activity', label: 'activity' },
] as const

export function AdminPage() {
  const [tab, setTab] = useState<'overview' | 'people' | 'activity'>('overview')

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Everyone's workload across every entity, and the accounts that can sign in."
      />

      <TabStrip tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && <OverviewTab />}
      {tab === 'people' && <PeopleTab />}
      {tab === 'activity' && <ActivityTab />}
    </>
  )
}

function OverviewTab() {
  const { data, isLoading, error } = useAdminOverview()

  if (isLoading) return <Loading />
  if (error) return <ErrorNotice error={error} />
  if (!data) return null

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Stat label="Open tasks" value={data.totals.openTasks} />
        <Stat
          label="Overdue"
          value={data.totals.overdueTasks}
          tone={data.totals.overdueTasks > 0 ? 'danger' : 'good'}
        />
        <Stat
          label="Unassigned"
          value={data.totals.unassignedTasks}
          tone={data.totals.unassignedTasks > 0 ? 'warn' : 'default'}
        />
        <Stat label="Active projects" value={data.totals.activeProjects} />
        <Stat label="Companies" value={data.totals.activeCompanies} />
        <Stat label="People" value={data.totals.activePeople} />
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Workload by person</h2>
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs tracking-wide text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">Person</th>
                <th className="px-4 py-2 text-right font-medium">Open</th>
                <th className="px-4 py-2 text-right font-medium">Overdue</th>
                <th className="px-4 py-2 text-right font-medium">Due this week</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.people.map((person) => (
                <tr key={person.id} className="row-alt">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar name={person.name} />
                      <span className="font-medium">{person.name}</span>
                      {person.role === 'ADMIN' && <Chip>admin</Chip>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{person.open}</td>
                  <td
                    className={cx(
                      'px-4 py-2.5 text-right tabular-nums',
                      person.overdue > 0 && 'font-semibold text-red-600 dark:text-red-400',
                    )}
                  >
                    {person.overdue}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{person.dueThisWeek}</td>
                </tr>
              ))}
              {data.people.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
                    No active people yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Load by entity</h2>
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs tracking-wide text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">Company</th>
                <th className="px-4 py-2 text-right font-medium">Projects</th>
                <th className="px-4 py-2 text-right font-medium">Open</th>
                <th className="px-4 py-2 text-right font-medium">Overdue</th>
                <th className="px-4 py-2 text-right font-medium">Unassigned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.companies.map((company) => (
                <tr key={company.id} className="row-alt">
                  <td className="px-4 py-2.5 font-medium">{company.name}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{company.projectCount}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{company.open}</td>
                  <td
                    className={cx(
                      'px-4 py-2.5 text-right tabular-nums',
                      company.overdue > 0 && 'font-semibold text-red-600 dark:text-red-400',
                    )}
                  >
                    {company.overdue}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{company.unassigned}</td>
                </tr>
              ))}
              {data.companies.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-slate-500 dark:text-slate-400">
                    No companies yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function PeopleTab() {
  const { data: users, isLoading, error } = useAdminUsers()
  const { data: companies } = useCompanies()
  const [creating, setCreating] = useState(false)

  /**
   * The scheduling right lives on the membership, and only /api/companies
   * carries it, so the flag is read back off the company list rather than off
   * each person. An administrator sees every company, and the query is already
   * cached for the add form, so this costs nothing extra.
   */
  const schedulerCompanies = useMemo(() => {
    const byUser = new Map<string, Set<string>>()
    for (const company of companies ?? []) {
      for (const member of company.members) {
        if (!member.canSchedule) continue
        const owned = byUser.get(member.id) ?? new Set<string>()
        owned.add(company.id)
        byUser.set(member.id, owned)
      }
    }
    return byUser
  }, [companies])

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Cancel' : 'Add person'}
        </Button>
      </div>

      {creating && <NewUserForm onDone={() => setCreating(false)} />}

      {isLoading && <Loading />}
      {error && <ErrorNotice error={error} />}

      <div className="space-y-3">
        {users?.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            schedulerCompanyIds={schedulerCompanies.get(user.id)}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * A generated password is shown exactly once, here, for the administrator to
 * pass on however they like. Nothing is emailed.
 */
function TempPasswordNotice({ password, name }: { password: string; name: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/50">
      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
        Temporary password for {name}
      </p>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
        Copy this now. It is not stored anywhere and cannot be shown again. Send it to them however
        you normally would; they must change it when they first sign in.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="rounded bg-white px-2 py-1 font-mono text-sm tracking-wide dark:bg-slate-900">
          {password}
        </code>
        <Button
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(password).then(() => setCopied(true))
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}

function NewUserForm({ onDone }: { onDone: () => void }) {
  const createUser = useCreateUser()
  const { data: companies } = useCompanies()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER')
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [result, setResult] = useState<{ name: string; tempPassword: string } | null>(null)

  function toggleCompany(companyId: string) {
    setSelected((prev) => {
      const next = { ...prev }
      if (companyId in next) delete next[companyId]
      else next[companyId] = ''
      return next
    })
  }

  if (result) {
    return (
      <div className="card p-4">
        <TempPasswordNotice password={result.tempPassword} name={result.name} />
        <div className="mt-3 flex gap-2">
          <Button
            variant="primary"
            onClick={() => {
              setResult(null)
              setEmail('')
              setName('')
              setSelected({})
            }}
          >
            Add another
          </Button>
          <Button onClick={onDone}>Done</Button>
        </div>
      </div>
    )
  }

  return (
    <form
      className="card space-y-3 p-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!email.trim() || !name.trim()) return
        createUser.mutate(
          {
            email: email.trim(),
            name: name.trim(),
            role,
            companies: Object.entries(selected).map(([companyId, title]) => ({
              companyId,
              title: title.trim() || undefined,
            })),
          },
          {
            onSuccess: (data) => setResult({ name: data.user.name, tempPassword: data.tempPassword }),
          },
        )
      }}
    >
      <h2 className="text-sm font-semibold">Add a person</h2>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="u-name">
            Full name
          </label>
          <input
            id="u-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="label" htmlFor="u-email">
            Email (used as the login)
          </label>
          <input
            id="u-email"
            type="email"
            className="field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="label" htmlFor="u-role">
            Access level
          </label>
          <select
            id="u-role"
            className="field"
            value={role}
            onChange={(e) => setRole(e.target.value as 'ADMIN' | 'MEMBER')}
          >
            <option value="MEMBER">Member</option>
            <option value="ADMIN">Administrator (sees everything)</option>
          </select>
        </div>
      </div>

      {companies && companies.length > 0 && (
        <fieldset>
          <legend className="label">Companies they work for</legend>
          <div className="space-y-2">
            {companies.map((company) => {
              const checked = company.id in selected
              return (
                <div key={company.id} className="flex flex-wrap items-center gap-2">
                  <label className="flex min-w-48 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCompany(company.id)}
                      className="rounded border-slate-300 dark:border-slate-600"
                    />
                    {company.name}
                  </label>
                  {checked && (
                    <input
                      className="field max-w-56"
                      placeholder="Title at this entity"
                      value={selected[company.id] ?? ''}
                      onChange={(e) =>
                        setSelected((prev) => ({ ...prev, [company.id]: e.target.value }))
                      }
                    />
                  )}
                </div>
              )
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Tick every entity this person is involved with. That is what decides which projects and
            tasks they can see.
          </p>
        </fieldset>
      )}

      {createUser.error && <ErrorNotice error={createUser.error} />}

      <div className="flex gap-2">
        <Button
          type="submit"
          variant="primary"
          loading={createUser.isPending}
          disabled={!email.trim() || !name.trim()}
        >
          Create account
        </Button>
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

function UserCard({
  user,
  schedulerCompanyIds,
}: {
  user: AdminUser
  /** Companies where this person holds the meeting scheduling right. */
  schedulerCompanyIds?: Set<string>
}) {
  const { user: me } = useAuth()
  const updateUser = useUpdateUser()
  const resetPassword = useResetPassword()
  const revokeSessions = useRevokeSessions()
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const [confirmingReset, setConfirmingReset] = useState(false)

  const isSelf = me?.id === user.id

  return (
    <div className={cx('card p-4', !user.isActive && 'opacity-70')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <Avatar name={user.name} size="md" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium">{user.name}</h3>
              {user.role === 'ADMIN' && <Chip>Administrator</Chip>}
              {!user.isActive && <Chip>Deactivated</Chip>}
              {user.mustChangePassword && <Chip title="Has not set their own password yet">Temp password</Chip>}
              {isSelf && <Chip>You</Chip>}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{user.email}</p>

            {user.companies.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                {user.companies.map((company, index) => (
                  <span key={company.id} className="inline-flex items-center gap-1.5">
                    {index > 0 && <span aria-hidden="true">·</span>}
                    <span>{company.title ? `${company.name} (${company.title})` : company.name}</span>
                    {schedulerCompanyIds?.has(company.id) && (
                      <Chip title="Can book meetings for people at this entity">Scheduler</Chip>
                    )}
                  </span>
                ))}
              </div>
            )}

            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              {user.lastLoginAt ? `Last signed in ${relativeTime(user.lastLoginAt)}` : 'Never signed in'}
              {' · '}
              {user.activeSessionCount} active session{user.activeSessionCount === 1 ? '' : 's'}
              {' · '}
              added {formatDate(user.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1 text-right">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-800 tabular-nums dark:text-slate-200">
              {user.workload.open}
            </span>{' '}
            open
          </p>
          {user.workload.overdue > 0 && (
            <p className="text-xs font-semibold text-red-600 dark:text-red-400">
              {user.workload.overdue} overdue
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
        <select
          className="field max-w-44"
          value={user.role}
          disabled={isSelf || updateUser.isPending}
          onChange={(e) => updateUser.mutate({ id: user.id, role: e.target.value as 'ADMIN' | 'MEMBER' })}
          aria-label={`Access level for ${user.name}`}
        >
          <option value="MEMBER">Member</option>
          <option value="ADMIN">Administrator</option>
        </select>

        <Button
          size="sm"
          disabled={isSelf}
          loading={updateUser.isPending}
          onClick={() => updateUser.mutate({ id: user.id, isActive: !user.isActive })}
        >
          {user.isActive ? 'Deactivate' : 'Reactivate'}
        </Button>

        {confirmingReset ? (
          <>
            <span className="text-xs text-slate-600 dark:text-slate-400">
              Reset their password? They will be signed out everywhere.
            </span>
            <Button
              variant="danger"
              size="sm"
              loading={resetPassword.isPending}
              onClick={() =>
                resetPassword.mutate(user.id, {
                  onSuccess: (data) => {
                    setTempPassword(data.tempPassword)
                    setConfirmingReset(false)
                  },
                })
              }
            >
              Yes, reset
            </Button>
            <Button size="sm" onClick={() => setConfirmingReset(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={() => setConfirmingReset(true)}>
            Reset password
          </Button>
        )}

        {user.activeSessionCount > 0 && (
          <Button
            size="sm"
            loading={revokeSessions.isPending}
            onClick={() => revokeSessions.mutate(user.id)}
            title="Force them to sign in again on every device"
          >
            Sign them out
          </Button>
        )}

        {isSelf && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            You cannot change your own access. That prevents locking yourself out.
          </span>
        )}
      </div>

      {(updateUser.error || resetPassword.error) && (
        <div className="mt-2">
          <ErrorNotice error={updateUser.error ?? resetPassword.error} />
        </div>
      )}

      {tempPassword && <TempPasswordNotice password={tempPassword} name={user.name} />}
    </div>
  )
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

/**
 * Forcing a booking through a known clash is the one action on this screen whose
 * entire point is the reason behind it, and "meeting override" on its own says
 * nothing about what was double booked or why. This reads the fields that action
 * writes and no others: a generic dump of `meta` would put every other action's
 * internals on screen, some of which are ids nobody needs to read.
 */
function overrideDetail(entry: ActivityEntry): { reason: string; over: string; who: string } | null {
  if (entry.action !== 'meeting_override') return null
  if (typeof entry.meta !== 'object' || entry.meta === null) return null

  const meta = entry.meta as Record<string, unknown>
  const reason = typeof meta.reason === 'string' ? meta.reason : ''
  if (!reason) return null

  return {
    reason,
    over: strings(meta.meetingTitles).join(', '),
    who: strings(meta.attendeeNames).join(', '),
  }
}

function ActivityTab() {
  const { data, isLoading, error } = useAdminActivity(60)

  if (isLoading) return <Loading />
  if (error) return <ErrorNotice error={error} />

  return (
    <div className="card divide-y divide-slate-100 dark:divide-slate-800">
      {data?.map((entry) => {
        const override = overrideDetail(entry)
        return (
        <div key={entry.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2.5 text-sm">
          <span className="font-medium">{entry.actor?.name ?? 'System'}</span>
          <span className="text-slate-600 dark:text-slate-400">
            {entry.action.replace(/_/g, ' ')}
          </span>
          <Chip>{entry.entityType}</Chip>
          <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
            {relativeTime(entry.createdAt)}
          </span>
          {override && (
            <p className="w-full text-xs text-slate-500 dark:text-slate-400">
              {override.who ? `${override.who} was already booked` : 'Someone was already booked'}
              {override.over ? ` (${override.over})` : ''}. Reason given: {override.reason}
            </p>
          )}
        </div>
        )
      })}
      {data?.length === 0 && (
        <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Nothing has happened yet.
        </p>
      )}
    </div>
  )
}
